import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast, getODataRows } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/dbsdefinitions';
const CUSTOMER_API = '/api/customers';

const CURRENCY_OPTIONS = [
  { value: 'TRY', label: 'TRY' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];
const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const EMPTY_FORM = {
  textId: '',
  customerId: '',
  bankName: '',
  totalLimit: '',
  dealerCashLoanAmount: '',
  definitionMonth: '',
  currencyCode: 'TRY',
  isActive: 'true',
  description: '',
  ddsInvoiceAmount: 0,
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function parseDecimal(value) {
  const parsed = Number.parseFloat(String(value || '').replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function monthInputToIso(value) {
  if (!value) return null;
  return new Date(`${value}-01T00:00:00`).toISOString();
}

function getDefaultDefinitionMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMoney(value, currencyCode = 'TRY') {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString('tr-TR', { style: 'currency', currency: currencyCode || 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '-';
}

// Net Limit = Toplam Limit − Bayi Nakit Kredi Kullanımı (canlı hesap)
function calculateNetLimit(form) {
  return parseDecimal(form.totalLimit) - parseDecimal(form.dealerCashLoanAmount);
}
// Kullanılabilir Limit = Net Limit − DDS Fatura Tutarı (canlı hesap)
function calculateAvailableLimit(form) {
  return calculateNetLimit(form) - parseDecimal(form.ddsInvoiceAmount);
}

function customerLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const name = item.title || item.Title || item.shortName || item.ShortName || '';
  return name ? `${code} - ${name}` : code;
}

// Bir DBS satırını (OData camelCase ya da entity PascalCase) form modeline çevirir.
function rowToForm(row) {
  return {
    textId: pick(row, 'textId', 'TextId') ?? '',
    customerId: pick(row, 'customerId', 'CustomerId') ?? pick(row.customer || row.Customer || {}, 'id', 'Id') ?? '',
    bankName: pick(row, 'bankName', 'BankName') ?? '',
    totalLimit: pick(row, 'totalLimit', 'TotalLimit') ?? '',
    dealerCashLoanAmount: pick(row, 'dealerCashLoanAmount', 'DealerCashLoanAmount') ?? '',
    definitionMonth: pick(row, 'definitionMonth', 'DefinitionMonth') ?? '',
    currencyCode: pick(row, 'currencyCode', 'CurrencyCode') ?? 'TRY',
    isActive: (pick(row, 'isActive', 'IsActive') !== false) ? 'true' : 'false',
    description: pick(row, 'description', 'Description') ?? '',
    ddsInvoiceAmount: pick(row, 'ddsInvoiceAmount', 'DdsInvoiceAmount') ?? 0,
  };
}

export function DbsDefinitionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni DBS Tanımı' : 'DBS Detayı', 'DBS tanımı kartı ve limit özeti.');

  const [form, setForm] = useState({ ...EMPTY_FORM, definitionMonth: monthInputToIso(getDefaultDefinitionMonth()) });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);
  const [customerLabels, setCustomerLabels] = useState({}); // id -> label (lazy)

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const customerName = useCallback((cid) => (cid ? (customerLabels[cid] || '…') : ''), [customerLabels]);

  // Müşteri etiketini id bazında lazy çek (4000+ müşteri → tümü yüklenmez).
  const ensureCustomerLabel = useCallback(async (cid) => {
    if (!cid) return;
    setCustomerLabels((prev) => (prev[cid] === undefined ? { ...prev, [cid]: '…' } : prev));
    try {
      const url = `${CUSTOMER_API}?$top=1&$filter=${encodeURIComponent(`Id eq ${cid}`)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        const c = getODataRows(data)[0];
        setCustomerLabels((prev) => ({ ...prev, [cid]: c ? customerLabel(c) : '-' }));
      } else {
        setCustomerLabels((prev) => ({ ...prev, [cid]: '-' }));
      }
    } catch {
      setCustomerLabels((prev) => ({ ...prev, [cid]: '-' }));
    }
  }, []);

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    // Listeden gelen satir varsa: aninda paint + early return. Ancak liste $select audit
    // alanlarini icermez -> audit'i tam kayittan (GetById) fire-and-forget cekip doldur.
    const row = location.state?.row;
    if (row) {
      const f0 = rowToForm(row);
      setForm(f0);
      if (f0.customerId) ensureCustomerLabel(f0.customerId);
      setLoading(false);
      fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const rec = unwrap(body);
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`DBS kaydı alınamadı (${res.status})`);
      const rec = unwrap(await res.json());
      if (!rec) throw new Error('DBS kaydı bulunamadı');
      const f = rowToForm(rec);
      setForm(f);
      setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
      if (f.customerId) ensureCustomerLabel(f.customerId);
    } catch (err) {
      showToast('error', err.message || 'DBS kaydı alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, ensureCustomerLabel]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /dbs/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  // location.key her navigasyonda değiştiği için bir klon yalnızca bir kez uygulanır.
  useEffect(() => {
    if (isNew && location.state?.clone) {
      const c = location.state.clone;
      setForm(c);
      setTried(false);
      if (c.customerId) ensureCustomerLabel(c.customerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildPayload = () => ({
    textId: form.textId.trim(),
    customerId: form.customerId,
    definitionMonth: form.definitionMonth || monthInputToIso(getDefaultDefinitionMonth()),
    bankName: form.bankName.trim(),
    totalLimit: parseDecimal(form.totalLimit),
    dealerCashLoanAmount: parseDecimal(form.dealerCashLoanAmount),
    ddsInvoiceAmount: parseDecimal(form.ddsInvoiceAmount),
    currencyCode: form.currencyCode,
    isActive: form.isActive === 'true',
    description: form.description.trim(),
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan
  // (önceki DBS backend'e kaydedilmiş olur). Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim()) { showToast('error', 'DBS referansı boş olamaz'); return; }
    if (!form.customerId) { showToast('error', 'Müşteri seçilmelidir'); return; }
    if (!form.bankName.trim()) { showToast('error', 'Banka adı zorunludur'); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || (isNew ? 'DBS kaydı eklenemedi' : 'DBS kaydı güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'DBS kaydı eklendi. Yeni kayda devam edebilirsiniz.');
          setForm({ ...EMPTY_FORM, definitionMonth: monthInputToIso(getDefaultDefinitionMonth()) });
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'DBS kaydı eklendi.');
        setTimeout(() => navigate('/dbs'), 400);
        return;
      }
      showToast('success', 'DBS kaydı güncellendi.');
      setTimeout(() => navigate('/dbs'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç.
  // Hiçbir şey kaydedilmez; kullanıcı "Kaydet" diyene kadar yeni DBS oluşmaz.
  const handleClone = () => {
    navigate('/dbs/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || 'DBS kaydı silinemedi');
      showToast('success', 'DBS kaydı silindi.');
      setTimeout(() => navigate('/dbs'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni DBS Tanımı' : (form.textId || 'DBS Detayı');
  const statusBadge = !isNew && (form.isActive === 'true'
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="bad">Pasif</StatusBadge>);

  const netLimit = useMemo(() => calculateNetLimit(form), [form]);
  const availableLimit = useMemo(() => calculateAvailableLimit(form), [form]);

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/dbs')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni DBS aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'DBS', to: '/dbs' }, { label: isNew ? 'Yeni DBS Tanımı' : 'DBS Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'DbsDefinition', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="wallet" title="DBS Bilgileri" subtitle="Referans, müşteri, banka ve durum">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="DBS Referansı" req mono />
            <FieldCustom label="Müşteri" req invalid={tried && !form.customerId}>
              <RelationPicker
                config={RELATION_CONFIGS.customer}
                value={form.customerId || ''}
                selectedLabel={form.customerId ? customerName(form.customerId) : ''}
                onChange={(val, item) => {
                  setForm((c) => ({ ...c, customerId: val }));
                  if (val && item) setCustomerLabels((prev) => ({ ...prev, [val]: customerLabel(item) }));
                }}
                placeholder="Müşteri ara…"
              />
            </FieldCustom>
            <FieldText form={form} set={set} tried={tried} k="bankName" label="Banka Adı" req />
            <FieldSelect form={form} set={set} k="currencyCode" label="Para Birimi" options={CURRENCY_OPTIONS} />
            <FieldText form={form} set={set} k="totalLimit" label="Toplam Limit" num />
            <FieldText form={form} set={set} k="dealerCashLoanAmount" label="Bayi Nakit Kredi Kullanımı (-)" num />
            <FieldSelect form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS} />
          </Grid>
        </Section>

        <Section
          icon="layers"
          title="Limit Özeti"
          subtitle="Net ve kullanılabilir limit anlık (canlı) hesaplanır"
          note="Net Limit = Toplam Limit − Bayi Nakit Kredi Kullanımı; Kullanılabilir Limit = Net Limit − DDS Fatura Tutarı."
        >
          <div className="dps-grid2">
            <div className="dps-ff">
              <div className="dps-ff-lbl">Net Limit</div>
              <div className="dps-ff-input"><input value={formatMoney(netLimit, form.currencyCode)} readOnly /></div>
            </div>
            <div className="dps-ff">
              <div className="dps-ff-lbl">Kullanılabilir Limit</div>
              <div className="dps-ff-input"><input value={formatMoney(availableLimit, form.currencyCode)} readOnly /></div>
            </div>
          </div>
        </Section>

        <Section icon="info" title="Açıklama" subtitle="Serbest not / açıklama">
          <Grid cols={2}>
            <FieldTextarea form={form} set={set} k="description" label="Açıklama" rows={3} span />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.textId}”</strong> DBS kaydını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default DbsDefinitionDetailPage;
